import React, { useEffect, useRef, useState, JSX } from 'react'

import { Contact, LandmarkIcon, Lock } from 'lucide-react'
import { usePathname, useSearchParams } from 'next/navigation'
import { mainnet } from 'viem/chains'

import { SplitsLink as Link } from '~/components/Link'
import {
  Copyable,
  NetworkDisplay,
  TokenDisplay,
  Tooltip,
} from '~/components/component-library'
import { AddressDisplay } from '~/components/component-library/AddressDisplay'
import { AddressDisplayAvatar } from '~/components/component-library/AddressDisplay/AddressDisplayAvatar'
import { AddressDisplayBadge } from '~/components/component-library/AddressDisplay/AddressDisplayBadges'
import { Button } from '~/components/component-library/Button'
import { useIsInsideDialog } from '~/components/component-library/Dialog'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  HoverCardPortal,
} from '~/components/ui/hover-card'
import { SupportedChainId } from '~/constants/chains'
import { useActiveOrg } from '~/hooks/org'
import { useAuthUser } from '~/hooks/useAuthUser'
import { useOrgContacts } from '~/hooks/useDashboard'
import { useDisplayName } from '~/hooks/useDisplayName'
import { usePrivacyMode } from '~/hooks/usePrivacyMode'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { HIDDEN_ADDRESS } from '~/store/privacyModeStore'
import { ContactSendRule, IAddress, TokenSendRule } from '~/types'
import { Address, SolanaAddress } from '~/utils/address'
import { getExplorerAddressLinkChainId } from '~/utils/addresses'
import { cn } from '~/utils/tailwind-utils'

const SHIMMER_CLASSES = cn(
  'hover:data-[state=closed]:animate-shimmer',
  'hover:data-[state=closed]:bg-[length:200%_100%]',
  'hover:data-[state=closed]:bg-clip-text',
  'hover:data-[state=closed]:[-webkit-background-clip:text]',
  'hover:data-[state=closed]:[-webkit-text-fill-color:transparent]',
  'hover:data-[state=closed]:bg-[linear-gradient(120deg,currentColor_30%,rgba(220,220,220,0.8)_50%,currentColor_70%)]',
  'dark:hover:data-[state=closed]:bg-[linear-gradient(120deg,currentColor_30%,rgba(120,120,120,0.9)_50%,currentColor_70%)]',
)

const AddressPopoverInner = ({
  address,
  chainId,
  children,
  fit,
  align = 'start',
  side = 'bottom',
  editDisabled = false,
}: {
  address: IAddress
  chainId?: SupportedChainId
  children: JSX.Element
  fit?: boolean
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'right' | 'bottom' | 'left'
  editDisabled?: boolean
}) => {
  const insideDialog = useIsInsideDialog()
  const [hasInteracted, setHasInteracted] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  // Use refs so setTimeout callbacks always read current values
  const isOverTriggerRef = useRef(false)
  const isOverContentRef = useRef(false)

  // Separate timeouts for open and close to avoid clobbering
  const openTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const cancelOpen = () => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = null
    }
  }

  const cancelClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }

  const handleTriggerEnter = () => {
    isOverTriggerRef.current = true
    if (!hasInteracted) setHasInteracted(true)
    cancelClose()
    if (!openTimeoutRef.current) {
      openTimeoutRef.current = setTimeout(() => {
        openTimeoutRef.current = null
        if (isOverTriggerRef.current || isOverContentRef.current) {
          setIsOpen(true)
        }
      }, 500)
    }
  }

  const scheduleClose = () => {
    if (!closeTimeoutRef.current) {
      closeTimeoutRef.current = setTimeout(() => {
        closeTimeoutRef.current = null
        if (!isOverTriggerRef.current && !isOverContentRef.current) {
          setIsOpen(false)
        }
      }, 100)
    }
  }

  const handleTriggerLeave = () => {
    isOverTriggerRef.current = false
    if (!isOverContentRef.current) {
      cancelOpen()
      scheduleClose()
    }
  }

  const handleContentEnter = () => {
    isOverContentRef.current = true
    cancelClose()
  }

  const handleContentLeave = () => {
    isOverContentRef.current = false
    if (!isOverTriggerRef.current) {
      scheduleClose()
    }
  }

  useEffect(() => {
    return () => {
      cancelOpen()
      cancelClose()
    }
  }, [])

  const triggerClassName = cn(
    fit
      ? 'inline w-fit'
      : 'inline-flex max-w-full min-w-0 items-center truncate',
  )

  // For now we won't show the address popover for Solana addresses
  if (SolanaAddress.validate(address)) {
    return <div className={triggerClassName}>{children}</div>
  }

  // Always mount the HoverCard tree to avoid DOM swaps that lose mouse
  // tracking. Only defer the portal content until first interaction.
  const hoverCardContent = isOpen ? (
    <AddressPopoverHoverContent
      address={address}
      chainId={chainId}
      align={align}
      side={side}
      handleMouseEnter={handleContentEnter}
      handleMouseLeave={handleContentLeave}
      editDisabled={editDisabled}
    />
  ) : null

  return (
    <HoverCard
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isOverTriggerRef.current && !isOverContentRef.current) {
          setIsOpen(false)
        }
      }}
    >
      <HoverCardTrigger
        asChild
        onMouseEnter={handleTriggerEnter}
        onMouseLeave={handleTriggerLeave}
      >
        <span className={cn(triggerClassName, SHIMMER_CLASSES)}>
          {children}
        </span>
      </HoverCardTrigger>
      {hasInteracted &&
        (insideDialog ? (
          hoverCardContent
        ) : (
          <HoverCardPortal>{hoverCardContent}</HoverCardPortal>
        ))}
    </HoverCard>
  )
}

const AddressPopoverHoverContent = ({
  address,
  chainId,
  align,
  side,
  handleMouseEnter,
  handleMouseLeave,
  editDisabled,
}: {
  address: IAddress
  chainId?: SupportedChainId
  align: 'start' | 'center' | 'end'
  side: 'top' | 'right' | 'bottom' | 'left'
  handleMouseEnter: () => void
  handleMouseLeave: () => void
  editDisabled?: boolean
}) => {
  return (
    <HoverCardContent
      align={align}
      side={side}
      sideOffset={8}
      className="cursor-default text-left"
      onClick={(event) => event.stopPropagation()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <AddressPopoverContent
        address={address}
        chainId={chainId}
        editDisabled={editDisabled}
      />
    </HoverCardContent>
  )
}

const AddressPopoverContent = ({
  address,
  chainId,
  editDisabled,
}: {
  address: IAddress
  chainId?: SupportedChainId
  editDisabled?: boolean
}) => {
  const {
    ens,
    smartAccountName,
    orgLabel,
    farcaster,
    payee,
    bankAccount,
    automation,
  } = useDisplayName(address, chainId)

  const { user: authUser } = useAuthUser()
  const { org } = useActiveOrg()
  const { contacts } = useOrgContacts()

  // The contact form returns here (query string included) on save or cancel.
  const pathname = usePathname()
  const search = useSearchParams().toString()
  const returnTo = search ? `${pathname}?${search}` : pathname

  const isInContacts = smartAccountName || orgLabel
  // TODO: add payee to contacts table
  const isPayee = payee
  const canAddContact = !smartAccountName

  const sendRules =
    contacts.find((entry) => Address.equal(entry.address, address))
      ?.sendRules ?? []
  const showRestrictions = sendRules.length > 0

  const isInternalAccount = authUser && smartAccountName && org
  const explorerUrl = getExplorerAddressLinkChainId(
    address,
    chainId || mainnet.id,
  )
  const isPrivacyModeOn = usePrivacyMode()

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex justify-between">
        <AddressDisplayAvatar address={address} chainId={chainId} size={40} />
        {authUser && canAddContact && !bankAccount && !editDisabled && (
          <Button
            variant="secondary"
            size="small"
            href={{
              path: orgLabel
                ? `/contacts/${address}?${new URLSearchParams({ returnTo })}`
                : `/contacts/new?${new URLSearchParams({ address, returnTo })}`,
            }}
          >
            {orgLabel ? 'Edit' : 'Add'} contact
          </Button>
        )}
      </div>
      <div className="flex flex-col space-y-0.5">
        <div className="font-medium md:text-base">
          <AddressDisplay address={address} layout="inline-text" hidePopover />
        </div>
        <div className="text-secondary">
          <Copyable
            layout="standard"
            value={address}
            displayValue={isPrivacyModeOn ? HIDDEN_ADDRESS : undefined}
            size="small"
            truncateMiddle={4}
            href={
              explorerUrl ? { path: explorerUrl, target: 'tab' } : undefined
            }
          />
        </div>
      </div>
      <div className="flex flex-col space-y-2 text-xs text-secondary">
        {isInternalAccount && (
          <Link
            path={`/accounts/${address}`}
            className="flex items-center space-x-2 hover:text-secondary"
          >
            <div className="flex-shrink-0">
              <AddressDisplayBadge
                variant={automation ? 'automation' : 'internal'}
              />
            </div>
            <div className="flex min-w-0 flex-1 items-baseline text-secondary">
              <span className="truncate font-medium text-primary">
                {org.name}
              </span>
              <span className="ml-1 flex-shrink-0">
                {automation ? 'automation account' : 'internal account'}
              </span>
            </div>
          </Link>
        )}
        {authUser && !isInternalAccount && (
          <div className="flex items-center space-x-2">
            {isInContacts ? (
              <AddressDisplayBadge variant="contact" />
            ) : (
              <div className="flex-shrink-0 rounded-md bg-gray-200 p-1 text-secondary opacity-50 dark:bg-gray-700">
                <Contact className="size-3" />
              </div>
            )}
            {isInContacts ? (
              <span className="text-secondary">
                <span className="font-medium text-primary">Saved</span> to
                contacts
              </span>
            ) : (
              <span className="text-secondary">Not saved to contacts</span>
            )}
          </div>
        )}
        {authUser && showRestrictions && (
          <SendRestrictionsRow sendRules={sendRules} />
        )}
        {authUser && isPayee && (
          <div className="flex items-center space-x-2">
            <AddressDisplayBadge
              variant={
                payee?.payeeOrg?.isCompliant
                  ? 'payee-verified'
                  : 'payee-unverified'
              }
            />
            <div className="text-secondary">
              <span
                className={cn('font-medium text-primary', {
                  'text-orange-600 dark:text-orange-300':
                    !payee?.payeeOrg?.isCompliant,
                })}
              >
                {!payee?.payeeOrg?.isCompliant
                  ? 'Unverified compliant recipient'
                  : 'Compliant recipient'}
              </span>
            </div>
          </div>
        )}
        {authUser && bankAccount && (
          <div className="flex items-center space-x-2">
            <LandmarkIcon className="h-3.5 w-3.5" />
            <div>
              <span className="font-medium text-primary">Bank account</span>
            </div>
          </div>
        )}
        {farcaster && (
          <Link
            path={`https://farcaster.xyz/${farcaster.username}`}
            target="tab"
          >
            <div className="flex items-center space-x-2">
              <AddressDisplayBadge variant="farcaster" />{' '}
              <div className="text-secondary">
                <span className="font-medium text-primary">
                  {farcaster.username}
                </span>{' '}
                on Farcaster
              </div>
            </div>
          </Link>
        )}
        {ens && (
          <Link path={`https://app.ens.domains/${ens}`} target="tab">
            <div className="flex items-center space-x-2">
              <AddressDisplayBadge variant="ens" />{' '}
              <div className="text-secondary">
                <span className="font-medium text-primary">{ens}</span> on ENS
              </div>
            </div>
          </Link>
        )}
      </div>
    </div>
  )
}

// There is no XS TokenDisplay, so a single rule reads as plaintext. Multiple
// rules collapse to a count with the full list in a tooltip.
const SendRestrictionsRow = ({
  sendRules,
}: {
  sendRules: ContactSendRule[]
}) => {
  const [firstRule] = sendRules
  return (
    <div className="flex items-center space-x-2">
      <div className="flex-shrink-0 rounded-md bg-gray-200 p-1 text-secondary dark:bg-gray-700">
        <Lock className="size-3" />
      </div>
      {sendRules.length === 1 ? (
        firstRule.tokenAddress ? (
          <TokenRuleText rule={firstRule} />
        ) : (
          <span className="text-secondary">
            Restricted to{' '}
            <span className="font-medium text-primary">
              <NetworkDisplay
                layout="inline-text"
                chainId={firstRule.chainId}
              />
            </span>
          </span>
        )
      ) : (
        <Tooltip
          position="bottom"
          content={
            // px/py even out the Tooltip's asymmetric px-2.5 py-1.5 padding
            <div className="flex max-h-64 flex-col gap-2.5 overflow-y-auto px-0.5 py-1.5">
              {sendRules.map((rule) =>
                rule.tokenAddress ? (
                  <TokenDisplay
                    key={`${rule.chainId}-${rule.tokenAddress}`}
                    address={rule.tokenAddress}
                    chainId={rule.chainId}
                    layout="standard"
                    size="small"
                  />
                ) : (
                  <NetworkDisplay
                    key={rule.chainId}
                    layout="standard"
                    size="small"
                    chainId={rule.chainId}
                  />
                ),
              )}
            </div>
          }
        >
          {/* A button so keyboard focus can reveal the tooltip */}
          <button
            type="button"
            className="cursor-default underline decoration-dashed underline-offset-4"
          >
            <span className="text-secondary">
              Restricted to{' '}
              <span className="font-medium text-primary">
                {/* Rules are all-networks or all-tokens, never mixed. */}
                {sendRules.length}{' '}
                {firstRule.tokenAddress ? 'tokens' : 'networks'}
              </span>
            </span>
          </button>
        </Tooltip>
      )}
    </div>
  )
}

const TokenRuleText = ({ rule }: { rule: TokenSendRule }) => {
  const { symbol } = useTokenMetadata({
    chainId: rule.chainId,
    address: rule.tokenAddress,
  })
  return (
    <span className="text-secondary">
      Restricted to{' '}
      <span className="font-medium text-primary">
        {symbol} on{' '}
        <NetworkDisplay layout="inline-text" chainId={rule.chainId} />
      </span>
    </span>
  )
}
export const AddressPopover = React.memo(AddressPopoverInner)
